import React, { useState, useEffect, useCallback } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { Checkbox, ClipboardList, Trash2, Edit3 } from 'lucide-react';

const ProposalItemsTable = ({
  breakdown,
  rows,
  onRowsChange,
  currencyCode,
  onImport,
  isImporting,
  hasImported,
  isProjectBased,
}) => {
  // Local copy for inline edit before confirming
  const [localRows, setLocalRows] = useState(rows);

  // Sync when rows prop changes (e.g., initial load or reset)
  useEffect(() => {
    setLocalRows(rows);
  }, [rows]);

  const formatCurrency = (amount, code = currencyCode) => {
    const locale = code === 'USD' ? 'en-US' : code === 'EUR' ? 'de-DE' : 'id-ID';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getPrefix = (code) => ({ USD: '$ ', EUR: '€ ', GBP: '£ ', JPY: '¥ ', SGD: 'S$ ', IDR: 'Rp ' }[code] || 'Rp ');

  const handleToggle = useCallback(
    (id) => {
      const updated = localRows.map((r) => (r.id === id ? { ...r, checked: !r.checked } : r));
      setLocalRows(updated);
      onRowsChange(updated);
    },
    [localRows, onRowsChange]
  );

  const handleSelectAll = useCallback(
    (checked) => {
      const updated = localRows.map((r) => ({ ...r, checked }));
      setLocalRows(updated);
      onRowsChange(updated);
    },
    [localRows, onRowsChange]
  );

  const handleFieldChange = useCallback(
    (id, field, rawValue) => {
      const value = parseFloat(rawValue) || 0;
      const updated = localRows.map((r) => {
        if (r.id !== id) return r;
        const updatedRow = { ...r, [field]: value };
        updatedRow.lineTotal = updatedRow.qty * updatedRow.unitPrice;
        return updatedRow;
      });
      setLocalRows(updated);
      onRowsChange(updated);
    },
    [localRows, onRowsChange]
  );

  const handleDelete = useCallback(
    (id) => {
      const updated = localRows.filter((r) => r.id !== id);
      setLocalRows(updated);
      onRowsChange(updated);
    },
    [localRows, onRowsChange]
  );

  const checkedRows = localRows.filter((r) => r.checked);
  // Selected Total is the sum of Unit Price (not Qty x Unit Price / lineTotal) — explicit request.
  const totalPreview = checkedRows.reduce((sum, r) => sum + r.unitPrice, 0);
  const hasRows = localRows.length > 0;
  const allChecked = hasRows && localRows.every((r) => r.checked);
  const someChecked = !allChecked && localRows.some((r) => r.checked);

  if (!isProjectBased || !breakdown) return null;

  return (
    <div className="card mt-4 proposal-items-table">
      {/* Header */}
      <div
        className="card-header"
        style={{
          background: 'linear-gradient(135deg, #0d3d42 0%, #1f656c 50%, #2a8a93 100%)',
          color: '#fff',
          borderRadius: 'var(--tblr-border-radius) var(--tblr-border-radius) 0 0)',
          padding: '12px 20px',
        }}
      >
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center gap-2">
            <ClipboardList size={20} />
            <h5 className="mb-0" style={{ color: '#fff' }}>
              Proposal Investment Breakdown
            </h5>
            {hasImported && (
              <span className="badge bg-secondary ms-2">Imported</span>
            )}
          </div>
          {!hasImported && hasRows && (
            <button
              className="btn btn-sm"
              style={{
                background: '#fff',
                color: '#0d3d42',
                border: 'none',
                fontWeight: 600,
              }}
              onClick={() => onImport(localRows)}
              disabled={isImporting || checkedRows.length === 0}
            >
              {isImporting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  />
                  Importing…
                </>
              ) : (
                <>Import Checked ({checkedRows.length}) — {formatCurrency(totalPreview, checkedRows[0]?.currencyCode || currencyCode)}</>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="card-body p-0">
        {!hasRows ? (
          <div className="text-center text-muted py-4">
            <ClipboardList size={32} className="mb-2 opacity-50" />
            <p className="mb-0">No proposal items to import.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-vcenter card-table table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someChecked;
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={hasImported}
                    />
                  </th>
                  <th>Description</th>
                  <th style={{ width: 80, textAlign: 'center' }}>Qty</th>
                  <th style={{ width: 80 }}>Unit</th>
                  <th style={{ width: 140 }}>Unit Price</th>
                  {!hasImported && <th style={{ width: 60 }} />}
                </tr>
              </thead>
              <tbody>
                {localRows.map((row) => (
                  <tr
                    key={row.id}
                    className={row.checked ? '' : 'text-muted'}
                    style={{ opacity: row.checked ? 1 : 0.5 }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={row.checked}
                        onChange={() => handleToggle(row.id)}
                        disabled={hasImported}
                      />
                    </td>
                    <td>
                      <div>{row.description}</div>
                      {row.yearlyPrices.length > 1 && (
                        <small className="text-muted">
                          {row.years
                            ?.map((yr, i) => `${yr}: ${formatCurrency(row.yearlyPrices[i])}`)
                            .join(' → ')}
                        </small>
                      )}
                    </td>
                    <td>
                      {hasImported ? (
                        <span className="text-center d-block">{row.qty}</span>
                      ) : (
                        <input
                          type="number"
                          className="form-control form-control-sm text-center"
                          value={row.qty}
                          min={1}
                          style={{ width: 70 }}
                          onChange={(e) => handleFieldChange(row.id, 'qty', e.target.value)}
                        />
                      )}
                    </td>
                    <td>
                      <span className="text-muted">{row.unit || '—'}</span>
                    </td>
                    <td>
                      {hasImported ? (
                        formatCurrency(row.unitPrice, row.currencyCode || currencyCode)
                      ) : (
                        <CurrencyInput
                          className="form-control form-control-sm"
                          value={row.unitPrice ?? 0}
                          decimalsLimit={2}
                          allowNegativeValue={false}
                          groupSeparator="."
                          decimalSeparator=","
                          prefix={getPrefix(row.currencyCode || currencyCode)}
                          onValueChange={(value) => handleFieldChange(row.id, 'unitPrice', value)}
                        />
                      )}
                    </td>
                    {!hasImported && (
                      <td>
                        <button
                          className="btn btn-icon btn-sm btn-outline-danger"
                          title="Remove row"
                          onClick={() => handleDelete(row.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {localRows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={4} className="text-end">
                      <strong>Selected Total:</strong>
                    </td>
                    <td>
                      <strong className="text-primary">
                        {formatCurrency(totalPreview, checkedRows[0]?.currencyCode || currencyCode)}
                      </strong>
                    </td>
                    {!hasImported && <td />}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProposalItemsTable;
